import { Grid, Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { AdminRoleId } from '@tassat/common-definitions';
import { UserRecord } from '@tassat/fred-definitions';
import { AdminUserRecordMapped } from '@tassat/fred-extended-definitions';
import React from 'react';

import { getApiCapailitiesListString, hasApiCapabilities } from 'Lib/apiConnectionsHelper';
import { formatEpochDate } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole } from 'Selectors/common';
import { getApiTypesByUserId } from 'Selectors/userApiTypes';
import { getUserById } from 'Selectors/users';

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: {
        padding: '0 2rem',
        marginTop: '1.5rem',
        marginBottom: '4rem'
    },
    detailRow: {
        flexBasis: 'auto' as const,
        padding: '.75rem .3rem',
        borderBottom: `1px solid ${theme?.app?.applicationColors.enumColors.grey ||
        '#888888'}`,
        '&:first-child': { paddingTop: 0 }
    },
    noNotes: { color: '#cfcfcf' },
    asset: { margin: '.2rem' }
});

interface UserStatisticsProps extends WithStyles<typeof muiStyles>{
    user: AdminUserRecordMapped | UserRecord;
}

const determineUserStatus = (user: UserRecord) => {
    if (user.IsFrozen) {
        return 'View Only';
    } else if (user.IsLocked) {
        return 'Locked';
    } else if (user.IsDeleted) {
        return 'Deleted';
    } else if (!user.IsTermsOfServiceApproved && !user.IsAdmin) {
        return 'Pending Verification';
    } else if (user.IsAdmin) {
        return 'Full Access';
    }

    return 'Full Access';
};

export function UserStatistics (props: UserStatisticsProps) {
    const {
        classes,
        user
    } = props;

    const userRole = useAppSelector(getCurrentRole);
    const modifiedByUser = useAppSelector((state) => getUserById(state, props.user.ModifiedBy, undefined, undefined, undefined));
    const userApiTypes = useAppSelector((state) => getApiTypesByUserId(state, false, props.user?.UserId || ''));

    return (
        <Grid
            className={classes.root}
            container
            direction='row'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                container
                direction='column'
                item
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                {!user.IsAdmin &&
                    <Grid
                        className={classes.detailRow}
                        container
                        direction='row'
                        item
                        justifyContent='space-between'
                        spacing={0}
                        xs={12}
                    >
                        <Typography variant='body2'>API Capabilities</Typography>
                        <Typography
                            className={hasApiCapabilities({
                            tokenApi: user.IsTokenAPIUser,
                            secondaryApiList: userApiTypes
                        }) ? '' : classes.noNotes}
                            id='userStatisticsUserApiCapabilities'
                            variant='body1'
                        >{getApiCapailitiesListString({
                            tokenApi: user.IsTokenAPIUser,
                            secondaryApiList: userApiTypes
                        }) || 'No API capabilities'}
                        </Typography>
                    </Grid>}
                {!user.IsAdmin &&
                    <Grid
                        className={classes.detailRow}
                        container
                        direction='row'
                        justifyContent='space-between'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Typography variant='body2'>Notes</Typography>
                        <Typography
                            className={user.Notes ? '' : classes.noNotes}
                            id='userStatisticsUserNotesValue'
                            variant='body1'
                        >{user.Notes || 'No notes'}
                        </Typography>
                    </Grid>}
                <Grid
                    className={classes.detailRow}
                    container
                    direction='row'
                    item
                    justifyContent='space-between'
                    spacing={0}
                    xs={12}
                >
                    <Typography variant='body2'>User Created</Typography>
                    <Typography
                        id='userStatisticsUserCreationTimeValue'
                        variant='body1'
                    >{formatEpochDate((user as UserRecord).CreatedAt)}
                    </Typography>
                </Grid>
                {user.IsAdmin &&
                    <Grid
                        className={classes.detailRow}
                        container
                        direction='row'
                        item
                        justifyContent='space-between'
                        spacing={0}
                        xs={12}
                    >
                        <Typography variant='body2'>Status</Typography>
                        <Typography
                            id='userStatisticsUserStatusValue'
                            variant='body1'
                        >{determineUserStatus(user as UserRecord)}
                        </Typography>
                    </Grid>}
                {!user.IsAdmin && user.ModifiedBy !== '0' && user.UpdatedAt !== '0' &&
                    <Grid
                        className={classes.detailRow}
                        container
                        direction='row'
                        item
                        justifyContent='space-between'
                        spacing={0}
                        xs={12}
                    >
                        <Typography variant='body2'>Last Modified</Typography>
                        <Typography
                            id='userStatisticsUserModifiedTimeValue'
                            variant='body1'
                        >{`${formatEpochDate(user.UpdatedAt)}${modifiedByUser && modifiedByUser.Username ? ` by ${modifiedByUser.Username}` : ''}`}
                        </Typography>
                    </Grid>}
                {(userRole.AdminRoleId === AdminRoleId.BANK_OPS || userRole.AdminRoleId === AdminRoleId.BANK_OPS_SUPERVISOR ||
                    userRole.AdminRoleId === AdminRoleId.CLIENT_SERVICES || userRole.AdminRoleId === AdminRoleId.HELP_DESK) &&
                    <Grid
                        className={classes.detailRow}
                        container
                        direction='row'
                        item
                        justifyContent='space-between'
                        spacing={0}
                        xs={12}
                    >
                        <Typography variant='body2'>Last Logon</Typography>
                        <Typography
                            id='userStatisticsUserLastLogonTimeValue'
                            variant='body1'
                        >
                            {formatEpochDate(user.LastLogin)}
                        </Typography>
                    </Grid>}
            </Grid>
        </Grid>
    );
}

export default withStyles(muiStyles)(UserStatistics);